import { HttpErrorResponse } from "@angular/common/http";
import { DestroyRef, Injectable, inject } from "@angular/core";
import { EventAggregator } from "@vp/data-access/application";
import { UserApiService } from "@vp/data-access/users";
import { CallLightDeactivatedEvent, PatientConsoleDeviceData, User } from "@vp/models";
import { NOTIFICATION_TRAY_ENABLED, NotificationService } from "@vp/shared/notification-service";
import { AppStoreService } from "@vp/shared/store/app";
import { EMPTY, Subscription, defer, from, of } from "rxjs";
import { catchError, distinctUntilChanged, map, mergeMap } from "rxjs/operators";

const RECONCILE_THROTTLE_MS = 60_000;
/** A persisted tray can hold hundreds of items; only the newest open lights are worth a request each. */
const RECONCILE_LIMIT = 20;
const RECONCILE_CONCURRENCY = 4;

/** Server timestamps are ISO strings; an unparseable one counts as "now" so the clear still lands. */
function parseTime(value: string | null | undefined): number {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : Date.now();
}

interface Clear {
  deviceUserId: string;
  by: string | null;
  at: number;
}

/**
 * Keeps the notification tray in step with the outside world: switches it with the
 * signed-in user, and resolves call lights that were cleared elsewhere, either live
 * (`CallLightDeactivatedEvent`) or on reload and tray open, when events may have
 * been missed. It never raises notifications.
 */
@Injectable({ providedIn: "root" })
export class NotificationTrayEffects {
  private readonly store = inject(NotificationService);
  private readonly appStore = inject(AppStoreService);
  private readonly userApi = inject(UserApiService);
  private readonly events = inject(EventAggregator);
  private readonly trayEnabled = inject(NOTIFICATION_TRAY_ENABLED);

  private readonly subscriptions = new Subscription();
  private started = false;
  private lastReconcileAt: number | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.subscriptions.unsubscribe());
  }

  /** Idempotent: the host calls it once, but a second call must not double-subscribe. */
  start(): void {
    // Nothing to keep in step while the tray is off: no persistence to load or switch,
    // and no stored call light to reconcile against the server.
    if (this.started || !this.trayEnabled()) {
      return;
    }
    this.started = true;

    this.subscriptions.add(
      this.appStore.loggedInUser$
        .pipe(
          map((user: User | null) => user?.userId ?? null),
          // The user object is re-emitted on every profile patch; only an identity change switches trays.
          distinctUntilChanged()
        )
        .subscribe(userId => {
          this.store.setUser(userId);
          if (userId !== null) {
            this.reconcile({ throttled: false });
          }
        })
    );

    // `on()` replays the current event and the server sends it even when nothing changed;
    // `markCleared` ignores clears older than an item's activation, so both are harmless.
    this.subscriptions.add(
      this.events
        .on<CallLightDeactivatedEvent>(CallLightDeactivatedEvent)
        .subscribe((event: CallLightDeactivatedEvent) => {
          const args = event.args;
          if (!args?.deviceUserId) {
            return;
          }
          const deviceData = args.patientConsoleDeviceData as
            PatientConsoleDeviceData | null | undefined;
          this.store.markCleared(
            args.deviceUserId,
            deviceData?.callLightData?.lastUpdatedByName ?? null,
            parseTime(args.deactivationDateTime)
          );
        })
    );
  }

  /**
   * Asks the server whether the newest open call lights are still on. `throttled` runs
   * (tray open) are skipped within a minute of the previous run, including the login run.
   */
  reconcile({ throttled }: { throttled: boolean }): void {
    if (!this.trayEnabled()) {
      return;
    }
    const now = Date.now();
    if (
      throttled &&
      this.lastReconcileAt !== null &&
      now - this.lastReconcileAt < RECONCILE_THROTTLE_MS
    ) {
      return;
    }
    this.lastReconcileAt = now;

    // The tray is newest first, so the slice keeps the newest open lights.
    const deviceUserIds = this.store
      .tray()
      .filter(n => n.kind === "call-light" && !n.resolved && !!n.deviceUserId)
      .slice(0, RECONCILE_LIMIT)
      .map(n => n.deviceUserId as string);
    if (deviceUserIds.length === 0) {
      return;
    }

    this.subscriptions.add(
      from(deviceUserIds)
        .pipe(
          mergeMap(
            deviceUserId =>
              // `defer` turns a synchronous throw into a per-item error the catch can swallow.
              defer(() => this.userApi.getUser(deviceUserId)).pipe(
                map((user): Clear | null => {
                  const callLight = user?.patientConsoleDeviceData?.callLightData;
                  return callLight?.callLightStatus === false
                    ? {
                        deviceUserId,
                        by: callLight.lastUpdatedByName ?? null,
                        at: parseTime(callLight.lastUpdatedDateTime)
                      }
                    : null;
                }),
                // A deleted device can never report its light off, so a 404 clears the item.
                // Any other failure leaves it as it is; the next reconcile retries it.
                catchError((error: unknown) =>
                  error instanceof HttpErrorResponse && error.status === 404
                    ? of<Clear>({ deviceUserId, by: null, at: Date.now() })
                    : EMPTY
                )
              ),
            RECONCILE_CONCURRENCY
          )
        )
        .subscribe(clear => {
          if (clear) {
            this.store.markCleared(clear.deviceUserId, clear.by, clear.at);
          }
        })
    );
  }
}
